import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Checkbox,
  Button,
  styled,
  InputAdornment,
  DialogActions,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { selectFilterOptions, selectTeamMemberFilters } from '../../../redux/teamMember/selector'
import * as filterActions from '../../../redux/teamMember/slice'
import { clearFilters } from '../../../redux/teamMember/slice'
import { TeamMemberFilters } from '../../../redux/teamMember/types'
import { ClearIcon } from '@mui/x-date-pickers'

type FilterOptionsType = {
  [key: string]: string[]
}

const SearchTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#f5f5f5',
  },
})

interface TeamRosterFilterProps {
  open: boolean
  onClose: () => void
}

export const TeamRosterFilter: React.FC<TeamRosterFilterProps> = ({ open, onClose }) => {
  const dispatch = useDispatch()
  const filterOptions = useSelector(selectFilterOptions) as FilterOptionsType
  const reduxFilters = useSelector(selectTeamMemberFilters)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('Supervisor')
  const [selectedFilters, setSelectedFilters] = useState<TeamMemberFilters>(reduxFilters)

  // Initialize state when modal opens or Redux filters change
  useEffect(() => {
    if (open) {
      const categories = Object.keys(filterOptions)
      if (!open) {
        setSelectedCategory(categories[0])
      }
      setSelectedFilters(reduxFilters)
    }
  }, [open, filterOptions, reduxFilters])

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category)
  }

  const categoryToKey = (category: string): keyof TeamMemberFilters => {
    switch (category) {
      case 'Access Role':
        return 'accessRole'
      case 'Practice':
        return 'practice'
      case 'Supervisor':
        return 'supervisor'
      case 'Operations Manager':
        return 'operationalManager'
      case 'Functional Area':
        return 'functionalArea'
      default:
        return 'practice' // fallback
    }
  }

  const handleOptionToggle = (option: string) => {
    const key = categoryToKey(selectedCategory)
    const currentFilters = selectedFilters[key] || []
    const newFilters = currentFilters.includes(option)
      ? currentFilters.filter((item) => item !== option)
      : [...currentFilters, option]

    // Update local state
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: newFilters,
    }))

    // Dispatch action immediately
    if (key === 'supervisor') {
      dispatch(filterActions.setSupervisorFilter(newFilters))
    } else if (key === 'operationalManager') {
      dispatch(filterActions.setOperationalManagerFilter(newFilters))
    } else if (key === 'practice') {
      dispatch(filterActions.setPracticeFilter(newFilters))
    } else if (key === 'functionalArea') {
      dispatch(filterActions.setFunctionalAreaFilter(newFilters))
    } else if (key === 'jobCode') {
      dispatch(filterActions.setJobCodeFilter(newFilters))
    } else if (key === 'accessRole') {
      dispatch(filterActions.setAccessRoleFilter(newFilters))
    }
  }

  const handleClearAll = () => {
    // Update local state
    setSelectedFilters({
      supervisor: [],
      operationalManager: [],
      practice: [],
      functionalArea: [],
      jobCode: [],
      accessRole: [],
    })
    setSearchQuery('')

    // Clear filters in Redux
    dispatch(clearFilters())
  }

  // Check if there are any active filters
  const hasActiveFilters = () => {
    return (
      selectedFilters.supervisor?.length > 0 ||
      selectedFilters.operationalManager?.length > 0 ||
      selectedFilters.practice?.length > 0 ||
      selectedFilters.functionalArea?.length > 0 ||
      selectedFilters.jobCode?.length > 0 ||
      selectedFilters.accessRole?.length > 0 ||
      searchQuery.trim() !== ''
    )
  }

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  const handleCancel = () => {
    // Clear all filters first
    setSelectedFilters({
      supervisor: [],
      operationalManager: [],
      practice: [],
      functionalArea: [],
      jobCode: [],
      accessRole: [],
    })
    setSearchQuery('')
    dispatch(clearFilters())
    
    // Then close the modal
    onClose()
  }

  const getCategoryOptions = (category: string) => {
    const options = filterOptions[category] || []
    if (!searchQuery.trim()) {
      return options
    }
    return options.filter((option) => option.toLowerCase().includes(searchQuery.toLowerCase().trim()))
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', padding: '16px 24px' }}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6'>Data Filters</Typography>
          <IconButton onClick={handleCancel} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box p={2}>
          <SearchTextField
            fullWidth
            placeholder='Search filter...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon color='action' />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton size='small' onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box display='flex' sx={{ height: '400px' }}>
          {/* Left sidebar - Categories */}
          <Box sx={{ 
            width: { xs: '120px', sm: '200px' }, 
            borderRight: '1px solid #e0e0e0', 
            overflowY: 'auto' 
          }}>
            <List disablePadding>
              {Object.keys(filterOptions).map((category: string) => (
                <ListItemButton
                  key={category}
                  selected={selectedCategory === category}
                  onClick={() => handleCategoryClick(category)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&:hover': {
                      backgroundColor: '#e0e0e0',
                    },
                  }}
                >
                  <ListItemText primary={category} />
                </ListItemButton>
              ))}
            </List>
          </Box>

          {/* Right side - Options */}
          <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
            <Typography variant='h6' gutterBottom>
              {selectedCategory}
            </Typography>
            <List>
              {getCategoryOptions(selectedCategory).map((option: string) => (
                <ListItem
                  key={option}
                  dense
                  onClick={() => handleOptionToggle(option)}
                  sx={{
                    borderRadius: '8px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  <Checkbox
                    checked={selectedFilters[categoryToKey(selectedCategory)]?.includes(option) || false}
                    size='small'
                  />
                  <ListItemText primary={option} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>

        {/* Footer */}
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '16px 24px',
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Button 
          color='info' 
          onClick={handleClearAll} 
          disabled={!hasActiveFilters()}
        >
          Clear all
        </Button>
        <Button 
          variant='contained'
          color='primary' 
          onClick={onClose}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TeamRosterFilter
